<template>
  <!-- unregister card -->
  <div class="unregister-warning">
    <div class="unregister-title">
      {{ $t('deviceUnregister.unregisterCard.warningTitle') }}
    </div>
    <div class="unregister-subtitle">
      {{ $t('deviceUnregister.unregisterCard.warningSubtitle') }}
    </div>
    <div class="unregister-message">
      <p v-if="hasSBD2" data-cy="unregisterMessageZero">
        {{ unregisterMessage['partZero'] }}
      </p>
      <p data-cy="unregisterMessageOne">
        {{ unregisterMessage['partOne'] }}
      </p>
      <p data-cy="unregisterMessageTwo">
        {{ unregisterMessage['partTwo'] }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { deviceModels, subscriptionTypes } from '@/store/subscriptions/utils'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'

export default defineComponent({
  name: 'DeviceUnregisterCard',
  props: {
    deviceId: String,
    useWebsite: { type: Boolean, default: true },
    deviceUnregistering: Boolean
  },
  data() {
    return {
      cache: null as {
        hasDataServices: boolean
        hasWebsiteAndDataServices: boolean
        hasSBD2: boolean
        onAPIPage: boolean
      } | null
    }
  },
  watch: {
    deviceUnregistering(newVal: boolean) {
      this.handleUnregistering(newVal)
    }
  },
  methods: {
    handleUnregistering(value: boolean) {
      if (value) {
        this.cache = {
          hasDataServices: this.hasDataServices,
          hasWebsiteAndDataServices: this.hasWebsiteAndDataServices,
          hasSBD2: this.hasSBD2,
          onAPIPage: this.onAPIPage
        }
      } else {
        this.cache = null
      }
    }
  },
  computed: {
    serial(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    isSmartBridge(): boolean {
      return (
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
          this.deviceId
        ) === deviceModels.SMART_BRIDGE
      )
    },
    hasDataServices(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_DATA_SERVICES_SUBSCRIPTION}`
      ](this.serial)
    },
    hasWebsite(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_WEBSITE_SUBSCRIPTION}`
      ](this.serial)
    },
    hasSBD2(): boolean {
      // SA-1426: use device store for all subscription types
      const subType = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_SUB_TYPE}`
      ](this.deviceId)
      return subType === subscriptionTypes.DEVICE_SMART_BRIDGE
    },
    hasWebsiteAndDataServices() {
      if (this.cache) {
        return this.cache.hasWebsiteAndDataServices
      }
      return this.hasWebsite && this.hasDataServices
    },
    onAPIPage() {
      if (this.cache) {
        return this.cache.onAPIPage
      }

      return this.$route.name === 'api-services'
    },
    unregisterMessage() {
      const localeStringPrefix = 'deviceUnregister.unregisterCard.messages.'
      const partZero = this.hasSBD2
        ? this.$t(localeStringPrefix + 'partZero.smartbridgeConnected')
        : ''
      let partOne = ''
      switch (true) {
        case this.useWebsite && this.hasWebsiteAndDataServices:
          partOne = this.$t(localeStringPrefix + 'partOne.dual.website') as string
          break
        case !this.useWebsite && this.hasWebsiteAndDataServices:
          partOne = this.$t(localeStringPrefix + 'partOne.dual.dataServices') as string
          break
        case this.hasWebsite:
          partOne = this.$t(localeStringPrefix + 'partOne.website') as string
          break
        case this.hasDataServices:
          partOne = this.$t(localeStringPrefix + 'partOne.dataServices') as string
          break
        case this.hasSBD2:
          partOne = this.$t(localeStringPrefix + 'partOne.smartbridgeConnected') as string
          break
        case this.isSmartBridge:
          partOne = this.$t(localeStringPrefix + 'partOne.smartbridgeDevice') as string
          break
        default:
      }
      const pageSpecifier = this.onAPIPage ? 'fromAPIPage' : 'notFromAPIPage'
      const partTwo = this.$t(localeStringPrefix + 'partTwo.' + pageSpecifier)
      return {
        partZero,
        partOne,
        partTwo
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.unregister-warning {
  min-height: 350px;
  .unregister-title {
    color: red;
    font-size: 18px;
  }
  .unregister-subtitle {
    color: red;
    font-size: 16px;
    margin-bottom: 16px;
  }
  .unregister-message {
    p {
      font-size: 14px;
      opacity: 0.86;
    }
  }
}
</style>
